import React, { useEffect, useState } from "react";

const AddDistrict = () => {
    const [data, setData] = useState(null);
    const [newDistrict, setNewDistrict] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [editingDistrict, setEditingDistrict] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");

    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

    // Fetch districts
    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                setError("");

                const res = await fetch(`${apiBaseUrl}/datadistricts`);

                if (!res.ok) {
                    throw new Error(`Failed to load districts (Status: ${res.status})`);
                }

                const result = await res.json();
                setData(result);
            } catch (err) {
                console.error("Fetch error:", err);
                setError("Failed to load districts. Please check your connection or refresh.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [apiBaseUrl]);

    const refreshData = async () => {
        try {
            const res = await fetch(`${apiBaseUrl}/datadistricts`);
            if (!res.ok) throw new Error("Refresh failed");
            const result = await res.json();
            setData(result);
        } catch (err) {
            alert("Could not refresh data. Please reload the page.");
        }
    };

    const getDistricts = () => {
        try {
            return Array.isArray(data?.states?.[0]?.districts) ? data.states[0].districts : [];
        } catch {
            return [];
        }
    };

    const districts = getDistricts();

    // Add new district
    const handleAddDistrict = async () => {
        const trimmed = newDistrict.trim();
        if (!trimmed) return alert("Please enter a district name");

        if (districts.some((d) => d.toLowerCase() === trimmed.toLowerCase())) {
            return alert(`"${trimmed}" already exists!`);
        }

        try {
            const res = await fetch(`${apiBaseUrl}/adddistricts`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ district: trimmed }),
            });

            const result = await res.json();
            if (!res.ok) throw new Error(result.error || "Failed to add district");

            setData(result.data);
            setNewDistrict("");
            alert("District added successfully!");
        } catch (err) {
            alert(err.message || "Failed to add district.");
        }
    };

    // Delete district → UPDATED TO /deletedistricts
    const handleDeleteDistrict = async (district) => {
        if (!window.confirm(`Delete "${district}" permanently? This cannot be undone.`)) return;

        try {
            const res = await fetch(`${apiBaseUrl}/deletedistricts`, {  // ← Changed to match backend
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ district }),
            });

            const result = await res.json();
            if (!res.ok) throw new Error(result.error || "Failed to delete district");

            setData(result.data);
            alert("District deleted successfully!");
        } catch (err) {
            alert(err.message || "Failed to delete district.");
        }
    };

    // Update district → UPDATED TO /updatedistricts
    const handleSaveEdit = async () => {
        const trimmed = editValue.trim();
        if (!trimmed) return alert("District name cannot be empty");
        if (trimmed === editingDistrict) return cancelEdit();

        if (districts.some((d) => d.toLowerCase() === trimmed.toLowerCase() && d !== editingDistrict)) {
            return alert("This district already exists!");
        }

        setIsSaving(true);
        try {
            const res = await fetch(`${apiBaseUrl}/updatedistricts`, {  // ← Changed to match backend
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    oldDistrict: editingDistrict,
                    newDistrict: trimmed,
                }),
            });

            const result = await res.json();
            if (!res.ok) throw new Error(result.error || "Failed to update district");

            setData(result.data);
            cancelEdit();
            alert("District updated successfully!");
        } catch (err) {
            alert(err.message || "Failed to update district.");
        } finally {
            setIsSaving(false);
        }
    };

    const cancelEdit = () => {
        setEditingDistrict(null);
        setEditValue("");
        setIsSaving(false);
    };

    const filteredDistricts = districts.filter((district) =>
        district.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (isLoading) return <p className="text-center py-20 text-gray-600 text-lg">Loading districts...</p>;
    if (error) return <p className="text-center py-20 text-red-600 text-lg">{error}</p>;

    return (
        <div className="min-h-screen bg-gray-100 py-8 px-4">
            <div className="max-w-2xl mx-auto bg-white shadow-lg rounded-xl p-6 md:p-8">
                <h1 className="text-3xl font-bold text-center text-gray-800 mb-8">Manage Districts</h1>

                {/* Add New */}
                <div className="flex flex-col sm:flex-row gap-3 mb-8">
                    <input
                        type="text"
                        value={newDistrict}
                        onChange={(e) => setNewDistrict(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleAddDistrict()}
                        placeholder="Add new district (e.g., Ernakulam)"
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                        onClick={handleAddDistrict}
                        className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition font-medium"
                    >
                        Add District
                    </button>
                </div>

                {/* Search */}
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search districts..."
                    className="w-full mb-6 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                {/* List */}
                <ul className="space-y-3">
                    {filteredDistricts.length === 0 ? (
                        <p className="text-center text-gray-500 py-8 text-lg">No districts found</p>
                    ) : (
                        filteredDistricts.map((district) => (
                            <li
                                key={district}
                                className="flex items-center justify-between bg-gray-50 p-4 rounded-lg border border-gray-200 hover:shadow-md transition"
                            >
                                {editingDistrict === district ? (
                                    <div className="flex items-center gap-3 flex-1">
                                        <input
                                            type="text"
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") handleSaveEdit();
                                                if (e.key === "Escape") cancelEdit();
                                            }}
                                            className="flex-1 px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            autoFocus
                                        />
                                        <button
                                            onClick={handleSaveEdit}
                                            disabled={isSaving}
                                            className={`px-5 py-2 text-sm font-medium text-white rounded transition ${
                                                isSaving
                                                    ? "bg-gray-400 cursor-not-allowed"
                                                    : "bg-green-500 hover:bg-green-600"
                                            }`}
                                        >
                                            {isSaving ? "Saving..." : "Save"}
                                        </button>
                                        <button
                                            onClick={cancelEdit}
                                            className="px-5 py-2 text-sm font-medium text-white bg-gray-500 rounded hover:bg-gray-600 transition"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <span className="font-medium text-gray-800">{district}</span>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => {
                                                    setEditingDistrict(district);
                                                    setEditValue(district);
                                                }}
                                                className="px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded hover:bg-blue-600 transition"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDeleteDistrict(district)}
                                                className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded hover:bg-red-600 transition"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </>
                                )}
                            </li>
                        ))
                    )}
                </ul>

                <div className="text-center mt-10">
                    <button
                        onClick={refreshData}
                        className="text-sm text-blue-600 hover:text-blue-800 underline font-medium"
                    >
                        Refresh List
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddDistrict;